import { useLocalStore } from 'mobx-react-lite';
import { Key, useEffect } from 'react';

import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { getBannedUsersForGroup } from 'APP/model/group/groupModel';
import bus from 'APP/packages/bus';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import type { User } from 'STORE/Users/User/User';

export interface IGroupBlackListPopupPresenterParams {
  groupId: string;
}

export interface IGroupBlackListPopupPresenter {
  ids: string[];
  group: Group | null;
  isLoading: boolean;
  cursor: number;
  CURSOR_STEP: number;
  menuRef: HTMLElement | null;
  activeUser: User | null;
  hasAdminRights: boolean;
  isOpenedMenu: boolean;
  hasMore: boolean;
  users: User[];
  updateCursor(): void;
  close(): void;
  init(): void;
  onEndReached(): void;
  toggleMenu(ref: HTMLElement | null, user: User): void;
  keyExtractor(blockedUser: User, index: number): Key;
}

export function useGroupBlackListPopupPresenter({
  params,
  popupInstance,
}: ICommonPopupsProps<IGroupBlackListPopupPresenterParams>): IGroupBlackListPopupPresenter {
  const { groupId } = params!;

  const presenter = useLocalStore<IGroupBlackListPopupPresenter>(() => ({
    CURSOR_STEP: 50,
    cursor: 0,
    isLoading: true,
    activeUser: null,
    menuRef: null,

    ids: [],

    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(groupId);
    },

    get hasAdminRights(): boolean {
      return !!presenter.group?.withMeInAdmins;
    },

    async init(): Promise<void> {
      presenter.isLoading = true;
      presenter.ids = [];

      if (!presenter.group?.id) {
        return;
      }

      try {
        const response = await getBannedUsersForGroup({
          groupId: presenter.group.id,
        });

        presenter.ids = [];

        if (response?.bannedUserIds?.length > 0) {
          presenter.ids = response.bannedUserIds;
        }
      } catch (error) {
        presenter.ids = [];
      }

      presenter.cursor = 0;
      presenter.updateCursor();
    },

    updateCursor: async (): Promise<void> => {
      presenter.isLoading = true;

      const nextCursor = presenter.cursor + presenter.CURSOR_STEP;

      const usersIds = presenter.ids.slice(presenter.cursor, nextCursor);

      if (usersIds.length > 0) {
        await Tasks.relationships.loadUnknownUsersByIds({
          usersIds,
        });
      }
      presenter.cursor = nextCursor;
      presenter.isLoading = false;
    },

    onEndReached(): void {
      presenter.updateCursor();
    },

    get users(): User[] {
      return presenter.ids
        .slice(0, presenter.cursor)
        .map((id) => Entities.usersStore.getUserById(id))
        .filter(Boolean) as User[];
    },

    toggleMenu(ref, user): void {
      if (presenter.activeUser !== user && ref) {
        presenter.menuRef = ref;
        presenter.activeUser = user;
      } else {
        presenter.menuRef = null;
        presenter.activeUser = null;
      }
    },

    get isOpenedMenu(): boolean {
      return !!presenter.activeUser;
    },

    get hasMore(): boolean {
      return presenter.cursor < presenter.ids.length;
    },

    close(): void {
      popupInstance.close();
    },

    keyExtractor(blockedUser): string {
      return blockedUser.id;
    },
  }));

  useEffect(() => {
    if (!presenter.hasAdminRights) {
      presenter.close();
    } else {
      presenter.init();
    }
  }, [presenter.hasAdminRights]);

  useEffect(() => {
    const unsubscribe = bus.group.subscribeParticipantsChanges(presenter.group?.id, () => {
      presenter.init();
    });

    return (): void => {
      unsubscribe();
    };
  }, [presenter.group?.id]);

  return presenter;
}
